/**
 * utilities/error.ts
 * desc: custom error class
 * note: used to extend error object with `status`, `code`, etc props.
 */
import { trackError } from './analytics';

export interface ApiErrorContext {
    error?: string;
    path?: string;
    [key: string]: unknown;
}

export interface ApiRequestErrorParams {
    context?: ApiErrorContext | null;
    errorCode?: string | null;
    id?: string | null;
    statusCode?: number;
    url?: string | null;
    internalDescription?: string | null;
}

// the wire shape consumed by tests and (later) the RTK Query error channel
export interface ApiErrorJson {
    context: ApiErrorContext | null;
    errorCode: string | null;
    id: string | null;
    message: string;
    name: string;
    statusCode: number;
    url: string | null;
    internalDescription: string | null;
}

class ApiRequestError extends Error {
    // `declare` keeps these type-only: without it oxc emits real ES2022 class
    // fields (own props defined before the ctor body runs), which reorders
    // enumerable properties and diverges from babel-jest, which strips them
    declare context: ApiErrorContext | null;
    declare errorCode: string | null;
    declare id: string | null;
    declare statusCode: number;
    declare url: string | null;
    declare internalDescription: string | null;

    // message can be undefined OR null (a failure body with null fields) —
    // Error stringifies null to "null", and that behavior is load-bearing
    constructor(message?: string | null, params: ApiRequestErrorParams = {}) {
        // @ts-expect-error Error's constructor is typed message?: string, but it
        // accepts and stringifies any value; null must pass through unchanged
        super(message);

        // captureStackTrace is a V8 extra (Chrome/Node only) — the runtime
        // guard handles other engines, and the local optional typing keeps us
        // off @types/node's ambient globals in browser code
        const ErrorCtor = Error as ErrorConstructor & { captureStackTrace?(target: object, ctor?: unknown): void };
        if (ErrorCtor.captureStackTrace) {
            ErrorCtor.captureStackTrace(this, ApiRequestError);
        }

        // set error instance properties
        this.name = this.constructor.name;
        this.context = params.context || null;
        this.errorCode = params.errorCode || null;
        this.id = params.id || null;
        this.statusCode = params.statusCode || 500;
        this.url = params.url || null;
        this.internalDescription = params.internalDescription || null;

        // if DEBUG set, log to console
        // note: debug is an env var found in "config.js"
        if (window.Ym.DEBUG) console.error(this.toJson());

        // trackError
        // note: re-format for tracking purposes
        trackError({
            context: params.context || null,
            id: params.id || null,
            name: params.context?.error || 'API Error',
            message: params.context?.path || 'unknown request url',
        });
    }

    toJson(): ApiErrorJson {
        return {
            context: this.context,
            errorCode: this.errorCode,
            id: this.id,
            message: this.message,
            name: this.name,
            statusCode: this.statusCode,
            url: this.url,
            internalDescription: this.internalDescription,
        };
    }
}

export default ApiRequestError;
